import {baseApi, TagType} from "./base-api";
import {getPagedResponse, PagedResponse} from "../../../utils/helpers";
import {
  WorkOrder,
  WorkOrderFormModel,
  WorkOrderHistory,
  WorkOrderStatusForm,
  WorkOrderUpdateFormModel
} from "../../data/entities/work-order";
import {WorkOrderLabour, WorkOrderLabourForm} from "../../data/entities/work-order-labour";
import {buildEntityTags, buildPagedResponseTags, invalidateTags} from "../../../utils/api-utils";
import {UpdateWorkOrderLogFiles, WorkOrderLog, WorkOrderLogForm,WorkLogMediaDto} from "../../data/entities/work-order-log";
import {JobPlan} from "../../data/entities/job-plan";
import {WorkOrderAsset, WorkOrderAssetForm, WorkOrderAssetHistory} from "../../data/entities/work-order-asset";
import {ApprovalStatusForm} from "../../data/models/approval-status-form";
import {ExcelExportResponse} from "../../data/models/excel-export-response";
import {
  WorkOrderCalculatedFieldsRequest,
  WorkOrderCalculatedFieldsResponse
} from "../../data/models/work-order-calculated-fields-request";

export const workOrderApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getWorkOrder: build.query<WorkOrder, { id: string, includeAssets?: boolean }>({
      query: ({id, includeAssets = true}) => ({
        url: `workOrders/getWorkOrder`,
        params: {
          workOrderId: id,
          includeAssets
        }
      }),
      providesTags: (result) => buildEntityTags(result, 'WorkOrder')
    }),
    getWorkOrderChild: build.query<WorkOrder, string>({
      query: (id) => ({
        url: `workOrders/getWorkOrderChild`,
        params: {
          workOrderId: id,
        }
      }),
      providesTags: (_result, _error, workOrderId) => [{type: 'WorkOrderChild', id: workOrderId}]
    }),
    getWorkOrders: build.query<PagedResponse<WorkOrder> & {priorityCounts: Record<string, number>}, Record<string, any>>({
      query: (params) => ({
        url: `workOrders/getWorkOrders`,
        params: params
      }),
      transformResponse: (response: any, meta: any) => {
        const pagination = JSON.parse(meta.response.headers.get("X-Pagination") as string);
        const priorityCounts = JSON.parse(meta.response.headers.get("PriorityCounts") as string);
        return {
          list: response,
          pagination: pagination,
          priorityCounts: priorityCounts
        };
      },
      providesTags: (result) => buildPagedResponseTags(result, 'WorkOrder')
    }),
    exportWorkOrders: build.query<ExcelExportResponse, Record<string, any>>({
      query: (params) => ({
        url: `workOrders/exportWorkOrders`,
        params: params
      })
    }),
    exportLabourList: build.query<ExcelExportResponse, Record<string, any>>({
      query: (params) => ({
        url: `workOrders/exportLabourList`,
        params: params
      })
    }),
    createWorkOrder: build.mutation<WorkOrder, WorkOrderFormModel>({
      query: (dto) => ({
        url: `workOrders/createWorkOrder`,
        method: 'POST',
        body: dto
      }),
      invalidatesTags: (_result, _error, {ticketId}) => [
        ...invalidateTags(ticketId, 'Ticket'),
        ...invalidateTags(_result.parentWorkOrderId, 'WorkOrderChild'),
        ...invalidateTags('LIST', 'WorkOrder')
      ]
    }),
    updateWorkOrder: build.mutation<WorkOrder, WorkOrderUpdateFormModel>({
      query: (dto) => ({
        url: `workOrders/updateWorkOrder`,
        method: 'PUT',
        body: dto
      }),
      invalidatesTags: (_result, _error, {id}) => invalidateTags(id, 'WorkOrder')
    }),
    deleteWorkOrder: build.mutation<void, string>({
      query: (id) => ({
        url: `workOrders/deleteWorkOrder`,
        method: 'DELETE',
        params: {
          workOrderId: id,
        }
      }),
      invalidatesTags: invalidateTags('LIST', 'WorkOrder')
    }),
    addWorkOrderLabour: build.mutation<WorkOrderLabour, WorkOrderLabourForm & {workOrderId: string}>({
      query: (dto) => ({
        url: `workOrders/addWorkOrderLabour/${dto.workOrderId}`,
        method: 'POST',
        body: dto
      }),
      invalidatesTags: () => invalidateTags(undefined, 'WorkOrderLabour')
    }),
    updateWorkOrderLabour: build.mutation<WorkOrderLabour, WorkOrderLabourForm & {workOrderId: string}>({
      query: (dto) => ({
        url: `workOrders/updateWorkOrderLabour/${dto.workOrderId}`,
        method: 'POST',
        body: dto
      }),
      invalidatesTags: () => invalidateTags(undefined, 'WorkOrderLabour')
    }),
    addWorkOrderLog: build.mutation<WorkOrderLog, WorkOrderLogForm & {workOrderId: string}>({
      query: (dto) => ({
        url: `workOrders/addWorkOrderLog/${dto.workOrderId}`,
        method: 'POST',
        body: dto
      }),
      invalidatesTags: () => invalidateTags(undefined, 'WorkOrderLog')
    }),
    addWorkOrderAsset: build.mutation<WorkOrderAsset, {workOrderId: string; assetId: string}>({
      query: (dto) => ({
        url: `workOrders/addWorkOrderAsset/${dto.workOrderId}`,
        method: 'POST',
        body: dto
      }),
      invalidatesTags: () => invalidateTags(undefined, 'WorkOrderAsset')
    }),
    getWorkOrderJobPlans: build.query<JobPlan[], string>({
      query: (id) => ({
        url: `workOrders/getWorkOrderJobPlans`,
        params: {
          workOrderId: id,
        }
      }),
      providesTags: (result) => buildPagedResponseTags(result, 'WorkOrder')
    }),
    getWorkOrderAssets: build.query<PagedResponse<WorkOrderAsset>, Record<string, any>>({
      query: (params) => ({
        url: `workOrders/getWorkOrderAssets`,
        params
      }),
      transformResponse: getPagedResponse,
      providesTags: (result) => buildPagedResponseTags(result, 'WorkOrderAsset')
    }),
    GetWorkOrderAssetOpenWorkOrders: build.query<PagedResponse<WorkOrder>, Record<string, any>>({
      query: (params) => ({
        url: `workOrders/getWorkOrderAssetOpenWorkOrders`,
        params
      }),
      transformResponse: getPagedResponse,
      providesTags: (result) => buildPagedResponseTags(result, 'WorkOrderAsset')
    }),
    GetWorkOrderHistory: build.query<WorkOrderHistory[],any>({
      query: (params) => ({
        url: `workOrders/getWorkOrderHistory`,
        params
      }),
    }),
    GetWorkOrderAssetHistory: build.query<WorkOrderAssetHistory[],any>({
      query: (params) => ({
        url: `workOrders/getWorkOrderAssetHistory`,
        params
      }),
    }),
    GetBuildingMapWorkOrders: build.query<PagedResponse<WorkOrder>, Record<string, any>>({
      query: (params) => ({
        url: `workOrders/getBuildingMapWorkOrders`,
        params
      }),
      transformResponse: getPagedResponse,
      providesTags: (result) => buildPagedResponseTags(result, 'WorkOrderAsset')
    }),
    GetAssetAllWorkOrders: build.query<PagedResponse<WorkOrder>, Record<string, any>>({
      query: (params) => ({
        url: `workOrders/getAssetAllWorkOrders`,
        params
      }),
      transformResponse: getPagedResponse,
      providesTags: (result) => buildPagedResponseTags(result, 'WorkOrderAsset')
    }),
    GetWorkOrderAssetFollowUpWorkOrders: build.query<PagedResponse<WorkOrder>, Record<string, any>>({
      query: (params) => ({
        url: `workOrders/getWorkOrderAssetFollowUpWorkOrders`,
        params
      }),
      transformResponse: getPagedResponse,
      providesTags: (result) => buildPagedResponseTags(result, 'WorkOrderAsset')
    }),
    findWorkOrderAsset: build.query<WorkOrderAsset | null, {workOrderId: string, qrCode?: string, searchTerm?: string, workOrderAssetId?: string}>({
      query: (params) => ({
        url: `workOrders/findWorkOrderAsset`,
        params
      }),
      providesTags: (result) => buildEntityTags(result, 'WorkOrderAsset')
    }),
    updateWorkOrderAsset: build.mutation<WorkOrderAsset, WorkOrderAssetForm>({
      query: (body) => ({
        url: `workOrders/updateWorkOrderAsset`,
        method: 'PUT',
        body
      }),
      invalidatesTags: (_result, _error, {id}) => invalidateTags(id, 'WorkOrderAsset')
    }),
    getWorkOrderLogs: build.query<WorkOrderLog[], string>({
      query: (id) => ({
        url: `workOrders/getWorkOrderLogs`,
        params: {
          workOrderId: id,
        }
      }),
      providesTags: (result) => [
        ...buildPagedResponseTags(result, 'WorkOrderLog'),
        ...result.map(({ originalLogEntryId }) => ({ type: 'WorkOrderLog' as TagType, id: originalLogEntryId })),
      ]
    }),
    getWorkOrderLabour: build.query<WorkOrderLabour[], string>({
      query: (id) => ({
        url: `workOrders/getWorkOrderLabour`,
        params: {
          workOrderId: id,
        }
      }),
      providesTags: (result) => buildPagedResponseTags(result, 'WorkOrderLabour')
    }),
    GetLabourList: build.query<PagedResponse<WorkOrderLabour>, Record<string, any>>({
      query: (params) => ({
        url: `workOrders/getLabourList`,
        params
      }),
      transformResponse: getPagedResponse,
      providesTags: (result) => buildPagedResponseTags(result, 'WorkOrderLabour')
    }),
    setWorkOrderStatus: build.mutation<void, WorkOrderStatusForm>({
      query: (body) => ({
        url: `workOrders/setWorkOrderStatus`,
        method: 'POST',
        body
      }),
      invalidatesTags: (_result, _error, {workOrderId}) => invalidateTags(workOrderId, 'WorkOrder')
    }),
    setWorkOrderApprovalStatus: build.mutation<void, ApprovalStatusForm>({
      query: (dto) => ({
        url: `workOrders/setApprovalStatus/${dto.entityId}`,
        method: 'POST',
        body: dto
      }),
      invalidatesTags: (_result, _error, {entityId}) => invalidateTags(entityId, 'WorkOrder')
    }),
    setConfirmJobPlan: build.mutation<void, any>({
      query: (dto) => ({
        url: `workOrders/setConfirmJobPlan/${dto.entityId}`,
        method: 'POST',
        body: dto
      }),
      invalidatesTags: (_result, _error, {entityId}) => invalidateTags(entityId, 'WorkOrder')
    }),
    addWorkLogFiles: build.mutation<void, UpdateWorkOrderLogFiles>({
      query: (data) => {
        const formData = new FormData();
        formData.append(`workOrderId`, data.workOrderId);
        formData.append(`workLogEntryId`, data.workLogEntryId);

        for (let i = 0; i < data.files.length; i++) {
          formData.append('Files', data.files[i]);
        }

        for (let i = 0; i < data.fileURIsToRemove.length; i++) {
          formData.append('FileURIsToRemove', data.fileURIsToRemove[i]);
        }

        return {
          url: `workOrders/addWorkLogFiles`,
          method: 'POST',
          body: formData
        }
      },
      invalidatesTags: (_result, _error, {workLogEntryId}) => invalidateTags(workLogEntryId, 'WorkOrderLog')
    }),
    addWorkLogMedia: build.mutation<void, WorkLogMediaDto>({
      query: (data) => {
        const formData = new FormData();
        formData.append("WorkLogFileDto.WorkOrderId", data.WorkLogFileDto.workOrderId);
        formData.append("WorkLogFileDto.WorkLogEntryId", data.WorkLogFileDto.workLogEntryId ?? "00000000-0000-0000-0000-000000000000");

        for (let i = 0; i < data.WorkLogFileDto.files.length; i++) {
          formData.append("WorkLogFileDto.Files", data.WorkLogFileDto.files[i]);
        }

        data.WorkLogFileDto.fileURIsToRemove.forEach((uri) =>
          formData.append("WorkLogFileDto.FileURIsToRemove", uri)
        );
        formData.append("NewWorkOrderLogDto.WorkOrderId", data.WorkLogFileDto.workOrderId);
        formData.append("NewWorkOrderLogDto.UserId", data.NewWorkOrderLogDto.userId);
        formData.append("NewWorkOrderLogDto.Comments", data.NewWorkOrderLogDto.comments);

        if (data.NewWorkOrderLogDto.originalLogEntryId) {
          formData.append("NewWorkOrderLogDto.OriginalLogEntryId", data.NewWorkOrderLogDto.originalLogEntryId);
        }
        console.log(formData);

        return {
          url: `workOrders/addWorkLogMedia`,
          method: "POST",
          body: formData,
        };
      },
    }),

    getWorkOrderCalculatedFields: build.mutation<WorkOrderCalculatedFieldsResponse, WorkOrderCalculatedFieldsRequest>({
      query: (formData) => ({
        url: `workOrders/getCalculatedFields`,
        method: 'POST',
        body: formData
      })
    }),
  }),
  overrideExisting: true,
});

export const {
  useLazyGetWorkOrdersQuery,
  useLazyGetLabourListQuery,
  useLazyGetWorkOrderQuery,
  useCreateWorkOrderMutation,
  useUpdateWorkOrderMutation,
  useDeleteWorkOrderMutation,
  useAddWorkOrderLabourMutation,
  useUpdateWorkOrderLabourMutation,
  useAddWorkOrderLogMutation,
  useAddWorkOrderAssetMutation,
  useLazyGetWorkOrderJobPlansQuery,
  useLazyGetWorkOrderAssetsQuery,
  useLazyGetWorkOrderHistoryQuery,
  useLazyGetWorkOrderAssetHistoryQuery,
  useLazyGetWorkOrderAssetOpenWorkOrdersQuery,
  useLazyGetBuildingMapWorkOrdersQuery,
  useLazyGetAssetAllWorkOrdersQuery,
  useLazyGetWorkOrderAssetFollowUpWorkOrdersQuery,
  useUpdateWorkOrderAssetMutation,
  useLazyGetWorkOrderLogsQuery,
  useLazyGetWorkOrderLabourQuery,
  useSetWorkOrderStatusMutation,
  useSetConfirmJobPlanMutation,
  useSetWorkOrderApprovalStatusMutation,
  useAddWorkLogFilesMutation,
  useAddWorkLogMediaMutation,
  useLazyFindWorkOrderAssetQuery,
  useFindWorkOrderAssetQuery,
  useGetWorkOrderQuery,
  useGetWorkOrderLabourQuery,
  useGetWorkOrderLogsQuery,
  useGetWorkOrderChildQuery,
  useLazyExportWorkOrdersQuery,
  useLazyExportLabourListQuery,
  useGetWorkOrderCalculatedFieldsMutation,
} = workOrderApi;
